import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { Box, Button, TextField, Typography } from "@mui/material";
import { AB_REPORT } from "../../graphql/queries/abReport";
import PageLoader from "../../components/PageLoader";
import Navbar from "../../components/NavBar";
import SectionTable from "./SectionTable";

// ISO timestamp -> "YYYY-MM-DD HH:mm UTC" (the whole report is UTC-based).
export const formatUtc = (iso) => {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return String(iso);
  return `${date.toISOString().slice(0, 16).replace("T", " ")} UTC`;
};

// Empty inputs mean "use the backend defaults" (epoch -> now), so they are
// left out of the variables rather than sent as empty strings.
const toVariables = ({ from, to }) => ({
  ...(from && { from }),
  ...(to && { to }),
});

const NoRecord = () => (
  <Typography align="center" variant="h6">
    No Record Found
  </Typography>
);

const AbReport = () => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [applied, setApplied] = useState({ from: "", to: "" });

  const { data, loading, error, refetch } = useQuery(AB_REPORT, {
    variables: toVariables(applied),
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const report = data?.abReport;
  const meta = report?.meta ?? {};
  const sections = Array.isArray(report?.sections) ? report.sections : [];
  const notes = Array.isArray(meta.notes) ? meta.notes : [];

  // The hook already surfaces a failed refetch as `error`; swallow the
  // duplicate promise rejection so Refresh stays usable in the error state.
  const handleRefresh = () => refetch().catch(() => {});

  if (loading) return <PageLoader />;

  return (
    <div>
      <Navbar title="A/B test" />
      <Box
        sx={{
          margin: "20px",
          display: "flex",
          columnGap: "20px",
          rowGap: "10px",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <TextField
          label="From"
          type="date"
          size="small"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="contained" onClick={() => setApplied({ from, to })}>
          Apply
        </Button>
        <Button variant="outlined" onClick={handleRefresh}>
          Refresh
        </Button>
      </Box>

      {error ? (
        // Bad windows are user errors (e.g. "Empty window: ..."); keep the
        // filters on screen so the dates can be corrected.
        <Typography component="p" sx={{ color: "red", margin: "20px" }}>
          {error.message}
        </Typography>
      ) : !report ? (
        <NoRecord />
      ) : (
        <>
          <Box sx={{ margin: "20px" }}>
            <Typography variant="h5">
              A/B test: {meta.experiment ?? "-"}
            </Typography>
            <Typography variant="body2">
              Epoch {formatUtc(meta.epoch)} · Window {formatUtc(meta.windowFrom)}{" "}
              → {formatUtc(meta.windowTo)} · Generated{" "}
              {formatUtc(meta.generatedAt)} · Day {meta.daysElapsed ?? "-"}
            </Typography>
            {notes.length > 0 && (
              <Typography variant="caption">{notes.join(" · ")}</Typography>
            )}
          </Box>

          {sections.map((section, index) => {
            const rows = Array.isArray(section?.rows) ? section.rows : [];
            const hasColumns =
              rows.length > 0 && Object.keys(rows[0] ?? {}).length > 0;
            return (
              <Box key={section?.key ?? index} sx={{ margin: "20px" }}>
                <Typography variant="h6" sx={{ marginBottom: "8px" }}>
                  {section?.title ?? section?.key ?? `Section ${index + 1}`}
                </Typography>
                {hasColumns ? <SectionTable rows={rows} /> : <NoRecord />}
              </Box>
            );
          })}
        </>
      )}
    </div>
  );
};

export default AbReport;
